/**
 * 確認對話框(REQ-20260729003)。
 *
 * 取代 `window.confirm` —— 瀏覽器原生對話框會鎖住整個分頁、樣式不受控、
 * 訊息只能是純文字(沒辦法把「這個動作要跑幾分鐘」之類的重點標出來),
 * 而且在 macOS Safari 上會擋住整個視窗頂端。
 *
 * 建在既有的 `Modal` 之上(ESC 關閉、鎖 body scroll 都已經在裡面),
 * 不另造一套。
 *
 * 按下確認後**立刻關閉**,實際工作交給呼叫端 —— 觸發備份的按鈕本來就會
 * 變成「備份中…」,不需要在對話框裡再做一次進度。
 */
import { Modal } from './Modal';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** 內文。接 ReactNode 讓呼叫端可以把關鍵字粗體、分行 */
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** danger = 紅色確認鈕(刪除類);預設 primary */
  tone?: 'primary' | 'danger';
  onConfirm: () => void;
  onClose: () => void;
  /**
   * 只在**按下取消鈕**時額外執行(REQ-20260903009),在 `onClose()` 之後。
   * 用途是「取消 = 回去補做某件事」的動線(例:把焦點送回沒填的區塊)——
   * 刻意不掛在 `onClose` 上:確認路徑也會 `onClose()`,順便搬走焦點會搶掉
   * 呼叫端接下來要做的事。ESC / 背景關閉走 `onClose` 不觸發。
   */
  onCancel?: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = '確定',
  cancelLabel = '取消',
  tone = 'primary',
  onConfirm,
  onClose,
  onCancel,
}: ConfirmDialogProps): JSX.Element {
  const confirmCls =
    tone === 'danger'
      ? 'bg-red-600 hover:bg-red-700'
      : 'bg-blue-600 hover:bg-blue-700';
  return (
    <Modal open={open} onClose={onClose} title={title} widthClass="max-w-md">
      <div className="grid gap-4">
        <div className="text-sm text-slate-700 leading-relaxed">{message}</div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              onClose();
              onCancel?.();
            }}
            className="border rounded px-3 py-1.5 text-slate-700 hover:bg-slate-100"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => {
              onClose();
              onConfirm();
            }}
            autoFocus
            className={`text-white rounded px-3 py-1.5 ${confirmCls}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}
